import { ApiRequestError } from "services/errors";
import { api, sendRequest } from "services/restUtil";
import { pollWithBackoff } from "utils/async";
import { apiQuery } from "utils/queries";
import { WithOptional } from "utils/types";

type CreateAiExercisePayload = {
  count: 1 | 2 | 3 | 4 | 5;
  context?: string | null;
  tally_conversation_id?: string | null;
  // At most five, and not together with tally_conversation_id.
  saved_word_ids?: string[];
};
type CreateExerciseData = WithOptional<CreateAiExercisePayload, "count"> | undefined;
type CreateAiExerciseResponse = {
  id: string;
};

const DEFAULT_EXERCISE_COUNT = 2;

export function createAiExercises(payload: CreateExerciseData) {
  return api.post<CreateAiExercisePayload, CreateAiExerciseResponse>("/ai/exercises", {
    count: DEFAULT_EXERCISE_COUNT,
    ...payload,
  });
}

export type AiExerciseSequence = {
  id: string;
  // How many sentences the round will hold when generation finishes; a round
  // that does not report it is complete as returned.
  requested_count?: number | null;
  sentences: AiExercise[]; // Should be called "exercises"
};

export type AiExercise = {
  id: string;
  sentence: string;
  context: string;
  answers: AiExerciseAnswer[];
  is_correct?: boolean | null;
  submitted_at?: string | null;
};

export type AiExerciseAnswer = {
  id: string;
  word: string;
  explanation: string;
  is_correct: boolean;
  is_submitted: boolean;
};

const fetchAiExerciseSequence = async (id: string) => {
  try {
    const response = await sendRequest("GET", `/ai/exercise/${id}`);

    if (response.status === 204) {
      throw new ExerciseNotReady("Exercise generation in progress");
    }
    return (await response.json()) as AiExerciseSequence;
  } catch (e) {
    if (e instanceof ApiRequestError && e.response?.status === 429) {
      // TODO how do we handle this?
      throw new TokenLimitReached("Limit reached");
    }
    throw e;
  }
};

export async function pollAiExerciseSequence(id: string) {
  return pollWithBackoff({
    fn: () => fetchAiExerciseSequence(id),
    timeout: 3 * 60 * 1000,
    minDelay: 2 * 1000,
    backoffFactor: 1.2,
    shouldRetry: ex => ex instanceof ExerciseNotReady,
  });
}

class TokenLimitReached extends Error {}
export class ExerciseNotReady extends Error {}

export const aiExercisesQuery = apiQuery<AiExerciseSequence, { id: string }>({
  key: "/ai/exercise/:id",
  queryFn: async ({ params }) => fetchAiExerciseSequence(params.id),
});

type AiExerciseSubmittedAnswers = {
  answers: Array<{
    sentence_id: string;
    answer_id: string;
  }>;
};

export type SubmitAiExerciseAnswersVariables = {
  sequenceId: string;
  exerciseId: string;
  answerId: string;
};

export function submitAiExerciseAnswers(variables: SubmitAiExerciseAnswersVariables) {
  return api.post<AiExerciseSubmittedAnswers, AiExerciseSequence>(
    `/ai/exercise/${variables.sequenceId}/answer`,
    {
      answers: [{ sentence_id: variables.exerciseId, answer_id: variables.answerId }],
    }
  );
}

export const getSubmittedAnswer = (exercise: AiExercise) =>
  exercise.answers.find(ans => ans.is_submitted);
